import { compose, filter, is, map, not, when } from 'ramda'
import HttpCacheAbstractAdapter from './HttpCacheAbstractAdapter'
import HttpCacheItem from '../container/HttpCacheItem'
import strategy from '../dictionary/strategy.json'

const isObject = is(Object)
const isNotArray = compose(not, is(Array))

/**
 * Cache adaptor for the volatile cache strategy.
 *
 * Use this strategy to ensure that data caching persists across pages.
 */
class HttpCacheLocalStorageAdapter extends HttpCacheAbstractAdapter {
  /**
   * Constructor
   *
   * @param {function} getData
   * @param {function} setData
   */
  constructor(getData, setData) {
    super(strategy.localStorage)
    this.localStorageKeyPrefix = 'httpcache'
    this.getData = getData
    this.setData = setData
  }

  /**
   * Loads all items for a cache pool.
   *
   * @param {HttpCachePoolConfig} poolConfig
   * @return {Array<HttpCacheItem>}
   */
  load(poolConfig) {
    return compose(
      filter(isObject),
      map((item) => (isObject(item) ? new HttpCacheItem(item.createdOn || null, item.expiresOn, item.type, item.id, item.value) : null)),
      when(isNotArray, () => []),
      this.getData,
    )(this.constructKey(poolConfig))
  }

  /**
   * Saves all items for a cache pool.
   *
   * @param {HttpCachePoolConfig} poolConfig
   * @param {Array<HttpCacheItem>} cacheItems
   * @return {Boolean}
   */
  save(poolConfig, cacheItems) {
    this.setData(this.constructKey(poolConfig), null, cacheItems)
    return true
  }

  /**
   * Creates the key for a pool's localstorage data.
   *
   * @param {HttpCachePoolConfig} poolConfig
   * @return {string}
   */
  constructKey(poolConfig) {
    return `${this.localStorageKeyPrefix}_${poolConfig.name}`
  }
}

export default HttpCacheLocalStorageAdapter
